import { useState, useEffect } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';

interface PayrollFormProps {
  employee: any;
  onClose: () => void;
}

export default function PayrollForm({ employee, onClose }: PayrollFormProps) {
  const [formData, setFormData] = useState({
    period: new Date().toISOString().slice(0, 7),
    hoursWorked: 151.67,
    overtimeHours: 0,
    absenceDays: 0,
    bonuses: 0,
    transportAllowance: 0,
    mealAllowance: 0,
    otherAllowances: 0,
    notes: ''
  });

  const [calculations, setCalculations] = useState({
    baseSalary: 0,
    overtimePay: 0,
    totalAllowances: 0,
    deductions: 0,
    grossSalary: 0,
    employeeCharges: 0,
    employerCharges: 0,
    netSalary: 0,
    totalEmployerCost: 0
  });

  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const EMPLOYEE_CHARGES_RATE = 0.22;
  const EMPLOYER_CHARGES_RATE = 0.42;
  const OVERTIME_RATE = 1.25;

  useEffect(() => {
    calculatePayroll();
  }, [formData, employee]);

  const calculatePayroll = () => {
    if (!employee) return;

    const baseSalary = employee.baseSalary;
    const hourlyRate = baseSalary / 151.67;

    const overtimePay = formData.overtimeHours * hourlyRate * OVERTIME_RATE;

    const totalAllowances = formData.bonuses + formData.transportAllowance + 
                           formData.mealAllowance + formData.otherAllowances;

    const deductions = formData.absenceDays * (hourlyRate * 7);

    const grossSalary = baseSalary + overtimePay + totalAllowances - deductions;

    const employeeCharges = grossSalary * EMPLOYEE_CHARGES_RATE;
    const employerCharges = grossSalary * EMPLOYER_CHARGES_RATE;
    const netSalary = grossSalary - employeeCharges;
    const totalEmployerCost = grossSalary + employerCharges;

    setCalculations({
      baseSalary,
      overtimePay,
      totalAllowances,
      deductions,
      grossSalary,
      employeeCharges,
      employerCharges,
      netSalary,
      totalEmployerCost
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('Hours') || name.includes('Days') ? 
              parseFloat(value) || 0 : 
              name.includes('Allowance') || name === 'bonuses' ? 
              parseFloat(value) || 0 : 
              value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.period) newErrors.period = 'La période est requise';
    if (formData.hoursWorked < 0) newErrors.hoursWorked = 'Les heures travaillées ne peuvent pas être négatives';
    if (formData.overtimeHours < 0) newErrors.overtimeHours = 'Les heures supplémentaires ne peuvent pas être négatives';
    if (formData.absenceDays < 0) newErrors.absenceDays = 'Les jours d\'absence ne peuvent pas être négatifs';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setApiError(null);

    const [year, month] = formData.period.split('-');

    const payload = {
      employe_id: employee.id,
      annee: parseInt(year),
      mois: parseInt(month),
      heures_travaillees: formData.hoursWorked,
      heures_supplementaires: formData.overtimeHours,
      jours_absence: formData.absenceDays,
      primes: formData.bonuses,
      indemnite_transport: formData.transportAllowance,
      indemnite_repas: formData.mealAllowance,
      autres_indemnites: formData.otherAllowances,
      salaire_base: employee.baseSalary,
      notes: formData.notes || null,
      // Les calculs sont envoyés pour information ou archivage
      salaire_brut: calculations.grossSalary,
      charges_salariales: calculations.employeeCharges,
      salaire_net: calculations.netSalary,
      cout_total_employeur: calculations.totalEmployerCost
    };

    try {
      await createResource('bulletins-paie', payload);

      alert('Fiche de paie créée avec succès !');
      onClose();

    } catch (error: any) {
      if (error.response?.data?.message) {
        setApiError(error.response.data.message);
      } else if (error.response?.data?.errors) {
        const msgs = Object.values(error.response.data.errors).flat().join(' ');
        setApiError(msgs);
      } else {
        setApiError('Erreur lors de l\'enregistrement. Veuillez réessayer.');
      }
      console.error('Erreur API:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!employee) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <p className="text-gray-500">Aucun employé sélectionné</p>
      </div>
    );
  }

  const formatGNF = (value: number) => 
    value.toLocaleString('fr-GN', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + ' GNF';

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Nouvelle Fiche de Paie - {employee.firstName} {employee.lastName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          {employee.position} - {employee.department}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-8">
        {/* Informations de base */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Informations de Base</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Période de Paie *
              </label>
              <input
                type="month"
                name="period"
                value={formData.period}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                  errors.period ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.period && <p className="mt-1 text-sm text-red-600">{errors.period}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Salaire de Base
              </label>
              <input
                type="text"
                value={formatGNF(employee.baseSalary)}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Taux Horaire
              </label>
              <input
                type="text"
                value={`${(employee.baseSalary / 151.67).toFixed(2).replace('.', ',')} GNF/h`}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Temps de travail */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Temps de Travail</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Heures Travaillées
              </label>
              <input
                type="number"
                name="hoursWorked"
                value={formData.hoursWorked}
                onChange={handleChange}
                min="0"
                step="0.5"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                  errors.hoursWorked ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="151.67"
              />
              {errors.hoursWorked && <p className="mt-1 text-sm text-red-600">{errors.hoursWorked}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Heures Supplémentaires
              </label>
              <input
                type="number"
                name="overtimeHours"
                value={formData.overtimeHours}
                onChange={handleChange}
                min="0"
                step="0.5"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                  errors.overtimeHours ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="0"
              />
              {errors.overtimeHours && <p className="mt-1 text-sm text-red-600">{errors.overtimeHours}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jours d'Absence
              </label>
              <input
                type="number"
                name="absenceDays"
                value={formData.absenceDays}
                onChange={handleChange}
                min="0"
                step="0.5"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                  errors.absenceDays ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="0"
              />
              {errors.absenceDays && <p className="mt-1 text-sm text-red-600">{errors.absenceDays}</p>}
            </div>
          </div>
        </div>

        {/* Primes et indemnités */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Primes et Indemnités</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primes (GNF)
              </label>
              <input
                type="number"
                name="bonuses"
                value={formData.bonuses}
                onChange={handleChange}
                min="0"
                step="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Indemnité Transport (GNF)
              </label>
              <input
                type="number"
                name="transportAllowance"
                value={formData.transportAllowance}
                onChange={handleChange}
                min="0"
                step="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Indemnité Repas (GNF)
              </label>
              <input
                type="number"
                name="mealAllowance"
                value={formData.mealAllowance}
                onChange={handleChange}
                min="0"
                step="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Autres Indemnités (GNF)
              </label>
              <input
                type="number"
                name="otherAllowances"
                value={formData.otherAllowances}
                onChange={handleChange}
                min="0"
                step="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="0"
              />
            </div>
          </div>
        </div>

        {/* Calculs automatiques */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Calculs Automatiques</h3>
          <div className="bg-gray-50 p-6 rounded-lg space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Salaire de base:</span>
                  <span className="text-sm font-medium text-gray-900">{formatGNF(calculations.baseSalary)}</span>
                </div>
                
                {calculations.overtimePay > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Heures supplémentaires:</span>
                    <span className="text-sm font-medium text-green-600">+{formatGNF(calculations.overtimePay)}</span>
                  </div>
                )}
                
                {calculations.totalAllowances > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Primes et indemnités:</span>
                    <span className="text-sm font-medium text-green-600">+{formatGNF(calculations.totalAllowances)}</span>
                  </div>
                )}
                
                {calculations.deductions > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Déductions (absences):</span>
                    <span className="text-sm font-medium text-red-600">-{formatGNF(calculations.deductions)}</span>
                  </div>
                )}
                
                <div className="border-t pt-2">
                  <div className="flex justify-between items-center font-medium">
                    <span className="text-sm text-gray-900">Salaire Brut Total:</span>
                    <span className="text-sm text-gray-900">{formatGNF(calculations.grossSalary)}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Charges salariales (22%):</span>
                  <span className="text-sm font-medium text-red-600">-{formatGNF(calculations.employeeCharges)}</span>
                </div>
                
                <div className="border-t pt-2">
                  <div className="flex justify-between items-center font-bold text-lg">
                    <span className="text-green-700">Salaire Net à Payer:</span>
                    <span className="text-green-700">{formatGNF(calculations.netSalary)}</span>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-300">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Charges patronales (42%):</span>
                    <span className="text-sm font-medium text-blue-600">{formatGNF(calculations.employerCharges)}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm font-medium text-gray-900">Coût total employeur:</span>
                    <span className="text-sm font-bold text-blue-600">{formatGNF(calculations.totalEmployerCost)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes (optionnel)
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            placeholder="Commentaires sur cette fiche de paie..."
          />
        </div>

        {/* Message d'erreur API */}
        {apiError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {apiError}
          </div>
        )}

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
          >
            {loading ? 'Enregistrement...' : 'Créer la Fiche de Paie'}
          </button>
        </div>
      </form>
    </div>
  );
}